import { useQuery } from '@tanstack/react-query';

import type { UseQueryResult } from '@tanstack/react-query';
import { BUOY_GPT_API } from 'src/interviews/requests/settings';
import type { IInterviewResults } from 'src/packages/polaris/types';
import type { RichEndingsProps } from '../components/RichEndings';
import type {
  IInterviewQuestion,
  ISupplierCardConfig,
  ITxocProps,
  MiniInterviewType,
} from 'src/packages/polaris/types';

enum RQInterviewType {
  DIFFERENTIAL = 'differential',
  PROTOCOL = 'protocol',
  DXOC = 'dxoc',
  PXOC = 'pxoc',
  TREATMENT_GUIDANCE = 'treatment_guidance',
  TXOC = 'txoc',
}
export type RelatedQuestionResult = {
  common_causes: Array<{ name: string; slug: string }> | null;
  chief_complaint: string | null;
  complaints: number[] | null;
  condition_name: string | null;
  custom_outcome_copy: Record<string, any>[] | null;
  dxoc: string | null;
  dxocs: number[] | null;
  interview_type: RQInterviewType;
  intro: string | null;
  question: string;
  questions: IInterviewQuestion[] | null;
  slug: string | null;
  supplier_cta_card_configs: ISupplierCardConfig[] | null;
  treatment: ITxocProps['treatment'] | null;
  triage_rule_considered: string | null;
};

export type RelatedQuestionType = Omit<RelatedQuestionResult, 'interview_type'> & {
  interview_type: MiniInterviewType;
};

type Response = {
  count: number;
  status: string;
  results: RelatedQuestionResult[];
};
type Body = {
  interview_type?: RQInterviewType;
  max_results?: number;
  results_token?: string;
  slug?: string;
  treatment?: string;
};

type Options = {
  key: string[];
  body: Body;
};

/** Create a related question using existing emergency & sx interview info */
const createRelatedSxQuestion = (props: RichEndingsProps) => ({
  common_causes: null,
  chief_complaint: '',
  complaints: props.symptomInterview?.configs?.[0]?.context?.complaints || [],
  condition_name: props.symptomInterview?.configs?.[0]?.context?.conditionName || '',
  custom_outcome_copy: null,
  dxoc: null,
  dxocs: null,
  interview_type: RQInterviewType.DIFFERENTIAL,
  intro: null,
  question: props.symptomInterview?.question,
  questions: null,
  slug: '/testing',
  supplier_cta_card_configs: null,
  treatment: null,
  triage_rule_considered: props.result?.triage?.token,
});

function useRelatedQuestions(props: RichEndingsProps) {
  const request = Request.create(props);
  const query = useQuery<Response>({
    queryKey: request?.key || [],
    queryFn: async () => {
      if (!request) {
        return;
      }
      const url = `${BUOY_GPT_API}/rich_ending/related_questions`;
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request.body),
        });
        if (!response.ok) {
          const errorBody = await response.json();
          throw new Error(`Error requesting for related questions: ${errorBody.message}`);
        }

        const data = await response.json();

        if (props.isAlarmContinuation) {
          return {
            ...data,
            count: data.count + 1,
            /*  When dismissing an alarming emergency interview, RQ endpoint doesn't return any options,
                but user can in fact dismiss alarm and move into sx interview, so we hardcode it here */
            results: [createRelatedSxQuestion(props), ...data.results],
          };
        }
        return data;
      } catch (error) {
        throw error;
      }
    },
    staleTime: Infinity,
    enabled: !!request,
  });

  return Request.deserialize(query);
}

class Request {
  static DEFAULT_BODY = {
    max_results: 4,
  };
  static create(options: RichEndingsProps): Options | undefined {
    const { interviewType, query, result } = options;

    switch (interviewType) {
      case 'dxoc':
        return this.#dxocRequest(result);
      case 'emergency':
        return this.#emergencyRequest(result);
      case 'symptoms':
        return this.#symptomsRequest(result);
      case 'treatmentGuidance':
        return this.#treatmentGuidanceRequest(query);
      case 'txoc':
        return this.#txocRequest(query);
      default: {
        console.warn(
          `Related questions for interview type: "${interviewType}" is not implemented.`,
        );
      }
    }
  }

  static deserialize(response: UseQueryResult<Response>) {
    const interviewMap = {
      [RQInterviewType.DIFFERENTIAL]: 'symptoms' as MiniInterviewType,
      [RQInterviewType.PROTOCOL]: 'emergency' as MiniInterviewType,
      [RQInterviewType.DXOC]: 'dxoc' as MiniInterviewType,
      [RQInterviewType.PXOC]: 'pxoc' as MiniInterviewType,
      [RQInterviewType.TREATMENT_GUIDANCE]: 'treatmentGuidance' as MiniInterviewType,
      [RQInterviewType.TXOC]: 'txoc' as MiniInterviewType,
    };
    return {
      data: {
        count: response.data?.count || 0,
        related_questions:
          response.data?.results?.map((r) => {
            return {
              ...r,
              interview_type: interviewMap[r.interview_type],
            };
          }) || [],
      },
      isError: response.isError,
      isLoading: response.isLoading,
      isSuccess: response.isSuccess,
    };
  }

  static #dxocRequest(result?: IInterviewResults) {
    const token = result?.token;
    if (!token) {
      return {
        key: [], // default key
        body: {}, // default params
      };
    }
    return {
      key: [`dxoc-relatedQuestions-${token}`],
      body: {
        ...this.DEFAULT_BODY,
        interview_type: RQInterviewType.DXOC,
        results_token: token,
      },
    };
  }
  static #emergencyRequest(result?: IInterviewResults) {
    const token = result?.token;
    if (!token) {
      return {
        key: [], // default key
        body: {}, // default params
      };
    }
    return {
      key: [`emergency-relatedQuestions-${token}`],
      body: {
        ...this.DEFAULT_BODY,
        interview_type: RQInterviewType.PROTOCOL,
        results_token: token,
      },
    };
  }

  static #symptomsRequest(result?: IInterviewResults) {
    const token = result?.token;
    if (!token) {
      return {
        key: [], // default key
        body: {}, // default params
      };
    }
    return {
      key: [
        `symptoms-relatedQuestions-${token}-${result?.differential?.map((diff) => diff.token)?.join('-')}`,
      ],
      body: {
        ...this.DEFAULT_BODY,
        interview_type: RQInterviewType.DIFFERENTIAL,
        results_token: token,
      },
    };
  }
  static #treatmentGuidanceRequest(query?: string) {
    if (!query) {
      return {
        key: [], // default key
        body: {}, // default params
      };
    }
    return {
      key: [`treatmentGuidance-relatedQuestions-${query}`],
      body: {
        ...this.DEFAULT_BODY,
        interview_type: RQInterviewType.TREATMENT_GUIDANCE,
        slug: query,
      },
    };
  }
  static #txocRequest(query?: string) {
    if (!query) {
      return {
        key: [], // default key
        body: {}, // default params
      };
    }
    return {
      key: [`txoc-relatedQuestions-${query}`],
      body: {
        ...this.DEFAULT_BODY,
        interview_type: RQInterviewType.TXOC,
        treatment: query,
      },
    };
  }
}

export default useRelatedQuestions;
